// @flow
/* eslint no-use-before-define:0 */

import randomTraceId from 'app/packages/trace-logger/utils/generateTraceId';

export type AnnotationTypes = 'BOOL' | 'I16' | 'I32' | 'I64' | 'STRING';

export type Annotation = {|
  key: string,
  timestamp: number,
|};

export type BinaryAnnotation = {|
  name: string,
  value: any,
  annotation_type: AnnotationTypes,
|};

type StopwatchOpts = {|
  name: string,
  metricName: string,
  coexistId?: string,
  endTime?: number,
  parent?: Stopwatch,
  spanId?: string | null,
  startTime?: number,
|};

const DEFAULT_STOP_TIMEOUT = 120000; // 120 second timeout default
type AnnotationMap = {
  [string]: { timestamp: number },
};

export type SpanJsonType = {|
  name: string,
  parent_id: null | string,
  timestamp: number,
  duration: number,
  id: string,
  annotations: AnnotationMap,
  binary_annotations: Array<BinaryAnnotation>,
  result: ?number,
|};

export default class Stopwatch {
  events: Array<Object>;
  name: string;
  uniqueName: string;
  metricName: string;
  coexistId: ?string;
  constraints: Array<string>;
  startTime: number;
  endTime: ?number;
  spanId: string;
  parent: ?Stopwatch;
  subStopwatchQueue: Array<Stopwatch>;
  annotations: AnnotationMap;
  binaryAnnotations: Array<BinaryAnnotation>;
  result: ?number;
  resultReason: ?string;
  isProcessing: boolean;

  constructor({ name, metricName, coexistId, startTime, endTime, parent, spanId }: StopwatchOpts) {
    this.events = [];
    this.name = name;
    this.metricName = metricName;
    this.coexistId = coexistId || null;
    this.uniqueName = `${name}${coexistId || ''}`;
    this.endTime = endTime || null;
    this.parent = parent || null;
    this.annotations = {};
    this.binaryAnnotations = [];
    this.result = null;
    this.resultReason = null;
    this.isProcessing = false;
    this.constraints = [];

    // If a `startTime` was specified, use this as the start time of the stopwatch
    // Otherwise use the UTC time now
    // Timestamps should be UTC in milliseconds
    this.startTime = startTime || Date.now();
    this.spanId = spanId || randomTraceId();

    // Array of stopwatches that go into calculating the stop time of this watch.
    this.subStopwatchQueue = [];
  }

  /**
   * Timestamps should be UTC in milliseconds
   */
  stopTiming(endTime: ?number) {
    this.endTime = endTime || Date.now();
  }

  /**
   * Specify a set of stopwatch keys that correspond to stopwatch names in the
   * subStopwatchQueue or annotations.
   *
   * This will populate the final `endTime` when all the stopwatches in this set
   * have `endTimes`
   *
   * Returns a promise that resolves when the subStopwatches have all completed or a timeout.
   */
  stopTimingConstrainedMax(): Promise<void> {
    return new Promise((resolve) => {
      let isDone = false;
      let subStopwatches = null;
      // Keep checking for the following things:
      // - `isProcessing`: If we're still processing, don't do anything and keep checking back til timeout
      // - `subStopwatches`: If we're no longer processing, set `subStopwatches` exactly once
      // - `isDone`: keep checking whether the substopwatches all have endTimes. Once they do, end the interval and
      // determine the end time.
      const subStopwatchQueueIntervalId = setInterval(() => {
        // When `isProcessing` flag is set to false, then we know all the substopwatches have been
        // added and we know the constraints of our timings
        // There should be something in subStopwatchQueue if we are doing a
        // constrained max for the stop timing. Adding this check ensures that
        // hf user nav tracing waits for the grid to have data before ending (michelle)
        if (!this.isProcessing && this.subStopwatchQueue.length) {
          if (!subStopwatches) {
            subStopwatches = this.subStopwatchQueue.filter((subStopwatch) =>
              this.constraints.some((constraint) => subStopwatch.name.startsWith(constraint)),
            );
          } else {
            isDone = subStopwatches.every((subStopwatch) => !!subStopwatch.endTime);
          }
          // Start calculating only when every substopwatch has a value and we're no longer waiting on
          // additional data (processing)
          if (isDone) {
            clearInterval(subStopwatchQueueIntervalId);

            const lastSubspanEnd = Math.max(0, ...subStopwatches.map((w) => w.endTime || 0));
            const lastAnnotationTime = this.getLastAnnotationTime(this.constraints);
            const maxEndTime = Math.max(lastSubspanEnd, lastAnnotationTime);

            if (maxEndTime) {
              if (lastSubspanEnd) {
                this.addAnnotation('lastSubspanEnd', lastSubspanEnd);
              }
              this.addBinaryAnnotation({
                name: 'lastEventIsSubspan',
                value: lastSubspanEnd >= lastAnnotationTime,
                annotation_type: 'BOOL',
              });
              this.setResult(PCONST.thrift.TracingClientTypes.PWTResult.COMPLETE);
            } else {
              // If we couldn't get an endtime, mark this timing as an error
              this.setResult(PCONST.thrift.TracingClientTypes.PWTResult.ERROR);
            }
            this.stopTiming(maxEndTime || Date.now());
            resolve();
          }
        }
      }, 1000);

      setTimeout(() => {
        clearInterval(subStopwatchQueueIntervalId);
        this.setResult(PCONST.thrift.TracingClientTypes.PWTResult.ERROR);
        this.addBinaryAnnotation({
          name: 'clientTimeout',
          value: true,
          annotation_type: 'BOOL',
        });
        this.stopTiming();
        resolve();
      }, DEFAULT_STOP_TIMEOUT);
    });
  }

  addSubStopwatch(stopwatch: Stopwatch) {
    stopwatch.parent = this;
    this.subStopwatchQueue.push(stopwatch);
    return stopwatch;
  }

  /**
   * Timestamps should be UTC in milliseconds
   */
  addAnnotation(value: string, timestamp?: number) {
    // If the annotation already exists, don't overwrite it
    if (this.annotations[value]) {
      return;
    }
    this.annotations[value] = { timestamp: timestamp || Date.now() };
  }

  addBinaryAnnotation({ name, value, annotation_type }: BinaryAnnotation) {
    this.binaryAnnotations.push({
      name,
      value,
      annotation_type,
    });
  }

  setResult(result: number, resultReason: ?string) {
    this.result = result;
    this.resultReason = resultReason;
  }

  addConstraints(keys: Array<string>) {
    this.constraints = this.constraints.concat(keys);
  }

  getLastAnnotationTime(constraints: Array<string>): number {
    const annotationTimestamps = Object.keys(this.annotations)
      .filter((name) => constraints.some((constraint) => name.startsWith(constraint)))
      .map((name) => this.annotations[name].timestamp || 0);
    return Math.max(0, ...annotationTimestamps);
  }

  /**
   * Returns a `spans` array which contains a flattened version of this stopwatch's trace data as well as all its children's.
   */
  getSpans(rootSpanName: string): null | Array<SpanJsonType> {
    const spans: Array<SpanJsonType> = [];

    // Traverse children and recursively add each of their span data to this flat `spans` array
    function addChildSpans(stopwatchTree: Stopwatch) {
      stopwatchTree.subStopwatchQueue.forEach((child: Stopwatch) => {
        const childSpan = child._toSpanJson();
        if (childSpan) {
          // If _toSpanJson() returns null, there was a problem with the endTime of this child.
          // TODO(jchan): handle this with some logging.
          spans.push(childSpan);
        }
        if (child.subStopwatchQueue.length) {
          addChildSpans(child);
        }
      });
    }

    const rootSpan = this._toSpanJson();
    if (!rootSpan) {
      // TODO(jchan): Handle exceptions like these with logging or something
      return null;
    }
    rootSpan.name = rootSpanName;
    spans.push(rootSpan);
    addChildSpans(this);
    return spans;
  }

  /**
   * Takes only this stopwatch's data and puts it in a JSON format that represents a pintrace span
   */
  _toSpanJson(): null | SpanJsonType {
    if (!this.endTime) {
      return null;
    }
    return {
      name: this.name,
      parent_id: this.parent ? this.parent.spanId : null,
      timestamp: this.startTime,
      duration: this.endTime - this.startTime,
      id: this.spanId,
      annotations: this.annotations,
      binary_annotations: this.binaryAnnotations,
      result: this.result,
    };
  }
}
